/**
 * Parse CSV file to array of objects
 */
export function parseCSV(csvText: string): Record<string, string>[] {
  const lines = csvText.split('\n').filter((line) => line.trim());
  if (lines.length === 0) return [];

  // Parse headers
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));

  // Parse rows
  const data: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === 0) continue;

    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    data.push(row);
  }

  return data;
}

/**
 * Parse a single CSV line, handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of value
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last value
  values.push(current.trim());

  return values;
}

/**
 * Validate asset import data
 */
export interface AssetImportRow {
  tag_id: string;
  description: string;
  brand: string;
  model: string;
  serial_number: string;
  status: string;
  cost?: string;
  purchase_date: string;
  purchased_from: string;
  warranty_period?: string;
  category?: string;
  location?: string;
  department?: string;
}

export interface ImportValidationResult {
  valid: boolean;
  errors: string[];
  data?: AssetImportRow[];
}

const REQUIRED_FIELDS = [
  'tag_id',
  'description',
  'brand',
  'model',
  'serial_number',
  'status',
  'purchase_date',
  'purchased_from',
];

const VALID_STATUSES = ['Available', 'Assigned', 'Maintenance', 'Retired', 'Lost'];

export function validateAssetImport(
  data: Record<string, string>[]
): ImportValidationResult {
  const errors: string[] = [];

  // Check if data is empty
  if (data.length === 0) {
    return {
      valid: false,
      errors: ['No data found in file'],
    };
  }

  // Check for required headers
  const headers = Object.keys(data[0]);
  const missingHeaders = REQUIRED_FIELDS.filter((field) => !headers.includes(field));

  if (missingHeaders.length > 0) {
    return {
      valid: false,
      errors: [`Missing required columns: ${missingHeaders.join(', ')}`],
    };
  }

  // Validate each row
  const validatedData: AssetImportRow[] = [];
  data.forEach((row, index) => {
    const rowNum = index + 2; // +2 because row 1 is headers and arrays are 0-indexed

    // Check required fields
    REQUIRED_FIELDS.forEach((field) => {
      if (!row[field] || row[field].trim() === '') {
        errors.push(`Row ${rowNum}: Missing required field '${field}'`);
      }
    });

    // Validate status
    if (row.status && !VALID_STATUSES.includes(row.status)) {
      errors.push(
        `Row ${rowNum}: Invalid status '${row.status}'. Must be one of: ${VALID_STATUSES.join(', ')}`
      );
    }

    // Validate cost if provided
    if (row.cost && row.cost.trim() !== '') {
      const cost = parseFloat(row.cost.replace(/[$,]/g, ''));
      if (isNaN(cost)) {
        errors.push(`Row ${rowNum}: Invalid cost value '${row.cost}'`);
      }
    }

    // Validate warranty_period if provided
    if (row.warranty_period && row.warranty_period.trim() !== '') {
      const warranty = parseInt(row.warranty_period);
      if (isNaN(warranty)) {
        errors.push(`Row ${rowNum}: Invalid warranty_period value '${row.warranty_period}'`);
      }
    }

    // Validate date format (YYYY-MM-DD)
    if (row.purchase_date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row.purchase_date)) {
        errors.push(
          `Row ${rowNum}: Invalid purchase_date format '${row.purchase_date}'. Use YYYY-MM-DD`
        );
      }
    }

    // Add to validated data if no errors for this row
    validatedData.push(row as AssetImportRow);
  });

  return {
    valid: errors.length === 0,
    errors,
    data: errors.length === 0 ? validatedData : undefined,
  };
}

/**
 * Read file as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
